import type { MenuItemCommandEvent } from 'primevue/menuitem';
import { Menu, type MenuItem, SubHeader } from '../types/menu';
import type { Action } from 'vince-core/types/roles';
import { usePermission, VinceApp } from 'vince-core/composables/usePermission';
import type { ResourceType } from 'vince-core/types/common';

export const useMenuStore = defineStore('menuStore', () => {
    const menuItems = ref<MenuItem[]>([]);
    const { hasPermission } = usePermission();

    function getItems (menu: Menu): MenuItem[] {
        return menuItems.value
            .filter((item: MenuItem) => isMenuItemVisible(item, menu))
            .sort((a, b) => a.position - b.position);
    }

    function isMenuItemVisible (item: MenuItem, menu: Menu): boolean {
        return item.menu === menu && (!item.requiredPermission || hasPermissionForItem(item));
    }

    function hasPermissionForItem (item: MenuItem): boolean {
        if (typeof item.requiredPermission === 'function') {
            return item.requiredPermission();
        } else {
            const { app, resource, resourceId, action } = item.requiredPermission!;
            return hasPermission(resource, resourceId, action, app);
        }
    }

    const mainMenu = computed(() => getItems(Menu.MAIN));
    const settingsMenu = computed(() => getItems(Menu.SETTINGS));
    const administrationMenu = computed(() => getItems(Menu.ADMINISTRATION));
    const infoMenu = computed(() => getItems(Menu.INFO));
    const appsMenu = computed(() => getItems(Menu.APPS));
    const helpResourcesMenu = computed(() => getItems(Menu.HELP_RESOURCES));
    function addMenuItem (
        labelKey: string,
        href: string,
        position: number,
        menu: Menu,
        subheader: SubHeader | undefined = undefined,
        icon: string | undefined = undefined,
        requiredPermission: {
            app: VinceApp,
            resource?: ResourceType,
            resourceId?: string,
            action?: Action | '*'
        } | (() => boolean) | undefined = undefined,
        command: ((event: MenuItemCommandEvent) => void) | undefined = undefined,
        count: (() => Promise<number>) | undefined = undefined

    ): MenuItem {
        // Avoid duplicate inserts
        const existingItem = menuItems.value.find(item => item.labelKey === labelKey && item.menu === menu);
        if (existingItem) {
            return existingItem;
        }

        const item: MenuItem = { labelKey, href, position, menu, subheader, icon, requiredPermission, command, count };
        menuItems.value.push(item);
        return item;
    }

    function removeMenuItem (href: string, menu: Menu) {
        menuItems.value = menuItems.value.filter(it => !(it.href === href && it.menu === menu));
    }

    function addAppMenuItem (
        label: string,
        href: string,
        position: number,
        requiredPermission: {
            app: VinceApp,
            resource?: ResourceType,
            resourceId?: string,
            action?: Action | '*'
        } | undefined = undefined
    ): MenuItem {
        const item: MenuItem = { label, href, position, menu: Menu.APPS, requiredPermission };
        menuItems.value.push(item);
        return item;
    }


    return { addMenuItem, addAppMenuItem, removeMenuItem, mainMenu, settingsMenu, administrationMenu, infoMenu, appsMenu, helpResourcesMenu, menuItems, isMenuItemVisible };
});
